import { FiDownload } from "react-icons/fi";
import type { HistoryItem } from "../../../types/smileDesign/historyItem";

interface HistoryCardProps {
    item: HistoryItem;
    index: number;
    isSelected: boolean;
    version: number;
    patientName: string;
    onSelect: (item: HistoryItem) => void;
    onDownload: (url: string, filename: string) => void;
    /** When comparing after images, shows A/B badge on selected cards */
    compareSlot?: "A" | "B" | null;
}

export default function HistoryCard({
    item,
    index,
    isSelected,
    version,
    patientName,
    onSelect,
    onDownload,
    compareSlot = null,
}: HistoryCardProps) {
    return (
        <div
            className={`rounded-[9px] overflow-hidden bg-[#26282C] border cursor-pointer transition-all duration-150 relative ${
                isSelected
                    ? "border-[#1DA8C6] shadow-[0_0_0_1px_#1DA8C6]"
                    : "border-white/[0.07] hover:border-white/[0.14] hover:-translate-y-0.5"
            }`}
            onClick={() => onSelect(item)}
        >
            <div className="relative aspect-[4/3.3] overflow-hidden">
                <img
                    src={item.image}
                    alt={`Design ${index + 1}`}
                    className="w-full h-full object-cover"
                />

                {compareSlot ? (
                    <div className="absolute top-2 left-2 bg-[#1DA8C6] text-[#08151A] text-xs font-bold px-2 py-[3px] rounded-full tracking-wide">
                        After {compareSlot}
                    </div>
                ) : (
                    isSelected && (
                        <div className="absolute top-2 left-2 bg-[#1DA8C6] text-[#08151A] text-xs font-bold px-2 py-[3px] rounded-full tracking-wide">
                            Active
                        </div>
                    )
                )}

                {item.wireframe && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1.5 max-w-[calc(100%-2.5rem)]">
                        <img
                            src={item.wireframe.image}
                            alt=""
                            className="w-7 h-7 rounded-[5px] object-cover border border-[#2fa9bf]/50 bg-black shrink-0"
                        />
                        {item.overlay && (
                            <img
                                src={item.overlay.image}
                                alt=""
                                className="w-7 h-7 rounded-[5px] object-cover border border-emerald-500/50 shrink-0"
                            />
                        )}
                        {item.restored && (
                            <img
                                src={item.restored.image}
                                alt=""
                                className="w-7 h-7 rounded-[5px] object-cover border border-violet-500/50 shrink-0"
                            />
                        )}
                        <span className="text-xs font-bold uppercase tracking-wide text-[#2fa9bf] bg-[rgba(10,11,13,0.75)] px-1.5 py-0.5 rounded truncate">
                            {item.restored
                                ? "WF + extend"
                                : item.overlay
                                  ? "WF + overlay"
                                  : "Wireframe"}
                        </span>
                    </div>
                )}

                {!isSelected && !compareSlot && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload(
                                item.image,
                                `${patientName}-${item.smileType}-V${version}-image.jpg`
                            );
                        }}
                        className="absolute bottom-2 right-2 w-[26px] h-[26px] rounded-[7px] bg-[rgba(10,11,13,0.7)] backdrop-blur-sm flex items-center justify-center text-white hover:bg-[rgba(29,168,198,0.8)] transition-colors"
                        aria-label="Download"
                    >
                        <FiDownload className="w-[13px] h-[13px]" />
                    </button>
                )}

                {!item.saved && !isSelected && !compareSlot && (
                    <div className="absolute top-2 right-2 bg-amber-500/90 text-black text-xs font-bold px-1.5 py-0.5 rounded-full uppercase">
                        Unsaved
                    </div>
                )}
                {item.saved &&
                    ((item.wireframe && !item.wireframe.saved) ||
                        (item.overlay && !item.overlay.saved) ||
                        (item.restored && !item.restored.saved)) &&
                    !isSelected &&
                    !compareSlot && (
                    <div className="absolute top-2 right-2 bg-amber-500/90 text-black text-xs font-bold px-1.5 py-0.5 rounded-full uppercase">
                        Assets unsaved
                    </div>
                )}
            </div>

            <div className="px-3 py-2.5">
                <p className="text-sm font-semibold">Design #{index + 1}</p>
                <p className="text-sm text-[#6B6C72] mt-0.5 truncate">
                    {item.smileType}
                    {item.wireframe ? " · +wireframe" : ""}
                    {item.overlay ? " · +overlay" : ""}
                    {item.restored ? " · +extend" : ""}
                </p>
            </div>
        </div>
    );
}
